import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from '@material-ui/core';
import { GetPaymentMethods, RemovePaymentMethod } from 'utils/payment';
import React, { useEffect, useState } from 'react';

import AddCard from 'components/Payment/AddCard';
import EditUser from 'components/EditUser/EditUser';
import Link from 'next/link';
import ModalContainer from 'components/Global/ModalContainer';
import NumberFormat from 'react-number-format';
import PaymentMethod from 'components/Payment/PaymentMethod';
import ShippingAddress from 'components/ShippingAddress/ShippingAddress';
import { formatImage } from 'utils/imageService';
import { logError } from 'utils/errorLogger';
import { useContent, useMjrApiUser } from 'utils/swrCalls';

/**
 * MyAccount
 *
 * Account page for viewing user information
 *
 */
const MyAccount = () => {
  const { data: content } = useContent();
  const { data: user } = useMjrApiUser();
  const bannerImage = `url(${formatImage(content?.content?.pages?.myAccount?.bannerImage)})`;
  const [paymentProfile, setPaymentProfile] = useState({});
  const [paymentLoading, setPaymentLoading] = useState(false);
  const { authNetCustomerProfileId } = user;
  const [confirmationDialogIsOpen, setConfirmationDialogIsOpen] = useState(false);
  const [confirmationDialogId, setConfirmationDialogId] = useState();

  const openCardRemoveConfirmationDialog = async (id) => {
    setConfirmationDialogId(id);
    setConfirmationDialogIsOpen(true);
  };

  const closeDialogWithoutChanges = async () => {
    setConfirmationDialogIsOpen(false);
  };

  const removeCardUsingIdFromState = async () => {
    // after remove recall get payments
    setConfirmationDialogIsOpen(false);
    setPaymentLoading(true);
    await RemovePaymentMethod(authNetCustomerProfileId, confirmationDialogId)
      .then((response) => {
        if (response) {
          GetPaymentMethods(authNetCustomerProfileId, setPaymentLoading, setPaymentProfile);
        } else {
          logError(response, 'HandleRemove(1) - /pages/my-account.js');
        }
        setPaymentLoading(false);
      })
      .catch((error) => logError(error, 'HandleRemove(2) - /pages/my-account.js'));
  };

  const HandleAdd = (authId) => {
    GetPaymentMethods(authId, setPaymentLoading, setPaymentProfile);
  };

  useEffect(() => {
    GetPaymentMethods(authNetCustomerProfileId, setPaymentLoading, setPaymentProfile);
  }, []);

  return (
    <div className='my-account-wrapper'>
      <div className='content-card banner-header' style={{ backgroundImage: bannerImage }}>
        <div className='content'>
          <Typography variant='h1'>{'My Account'}</Typography>
        </div>
      </div>
      <div className='content-card'>
        <Typography variant='h1'>{'Personal Information'}</Typography>
        <Typography variant='body1'>
          {'Name: '}
          {user.firstName}
          {' '}
          {user.lastName}
        </Typography>
        <Typography variant='body1'>
          {'Phone: '}
          <NumberFormat value={user.phone} displayType='text' format='(###) ###-####' allowEmptyFormatting mask='_' />
        </Typography>
        <Typography variant='body1'>
          {'email: '}
          {user.email}
        </Typography>
        <ModalContainer
          buttonText='Edit Personal Information'
          buttonID='edit-user-button'
          headerText='Update Personal Information'
          variant='contained'
          color='secondary'
        >
          <EditUser buttonText='Update' />
        </ModalContainer>
      </div>
      <div className='content-card'>
        <Typography variant='h1'>{'Password'}</Typography>
        <Link
          href='/api/auth/social-login'
        >
          <Button variant='contained' color='secondary' id='change-password-button'>
            {'Change Password'}
          </Button>
        </Link>
      </div>
      <div className='content-card'>
        <Typography variant='h1'>{'Shipping Address'}</Typography>
        <div className='address-wrapper'>
          <Typography variant='body1'>{user?.address?.addressLine1}</Typography>
          {user?.address?.addressLine2 === '' ? null : (
            <Typography variant='body1'>{user?.address?.addressLine2}</Typography>
          )}
          <Typography variant='body1'>
            {user?.address?.city}
            {', '}
            {user?.address?.state}
            {' '}
            {user?.address?.zipCode}
            {' '}
            {user?.address?.country}
          </Typography>
          <ModalContainer
            buttonText='Edit Address'
            buttonID='edit-shipping-button'
            headerText='Update Shipping Address'
            variant='contained'
            color='secondary'
          >
            <ShippingAddress type='edit_user' buttonText='Update' hideNameFields />
          </ModalContainer>
        </div>
      </div>
      <div className='content-card'>
        <Grid container spacing={3} className='card-header'>
          <Grid item xs={12} sm={6}>
            <Typography variant='h1'>{'Payment Information'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className='control-wrapper'>
              <ModalContainer
                headerText='Add Payment Method'
                buttonText='Add Payment Method'
                buttonID='add-payment-button'
                variant='contained'
                color='secondary'
              >
                <AddCard
                  type='add_card'
                  order=''
                  total=''
                  buttonText='Add Payment'
                  buttonID='add-payment-method-confirm-button'
                  showMembership={false}
                  showPromoCode={false}
                  callbackFunction={HandleAdd}
                />
              </ModalContainer>
            </div>
          </Grid>
        </Grid>
        <div className='card-wrapper'>
          {paymentLoading ? (
            <div className='center'>
              <CircularProgress size={20} />
            </div>
          ) : paymentProfile?.message === 'no_profile' ? (
            <Typography variant='body1'>{'No payment methods added'}</Typography>
          ) : (
            paymentProfile?.paymentProfiles
            && paymentProfile?.paymentProfiles.map((profile) => (
              <>
                <PaymentMethod HandleRemove={openCardRemoveConfirmationDialog} key={profile.customerPaymentProfileId} profile={profile} />
                <Dialog open={confirmationDialogIsOpen} onClose={closeDialogWithoutChanges}>
                  <DialogTitle>{'Are you sure you want to remove this card?'}</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      {'Removing this card will prevent you from using it in any future transactions.'}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={closeDialogWithoutChanges} color='primary'>
                      {'Keep'}
                    </Button>
                    <Button onClick={removeCardUsingIdFromState} color='primary' autoFocus>
                      {'Remove'}
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAccount;
